import { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from "react";
import api from "services/api";
import ls from "services/ls";
import moneyFormatter from "services/moneyFormatter";
import { __ } from "services/Translator";
import { CashierPreviewContext, DEFAULT_PREVIEW_VIP, DEFAULT_PREVIEW_BONUSES } from "./previewContext";

// Set true by the /cashier-preview root so the module-level saveNextBonus (which
// BonusesView imports directly, outside any hook/context) is inert in preview and
// never POSTs to the live backend.
let previewInert = false;
export function setPreviewInert(v) {
  previewInert = !!v;
}

// Deposit denominations shown when the CMS cashier_settings are absent. These
// match the Hub literals so the redesign never renders empty presets.
export const DEFAULT_DENOMINATIONS = [20, 50, 100, 250];

// Serving-contract badge whitelist (mirrors CashierModals). Canonical order used
// to filter + sort the badges rendered as pills on the method tile.
const BADGE_ORDER = ["instant", "recommended", "popular"];

// One key per deposit-submit attempt so a retry of the SAME attempt cannot
// double-charge once the server honors it. Mirrors StoreContent.makeIdempotencyKey.
export function makeIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "dep-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
}

// Shared read-once cashier data (VIP + bonuses) provided by the shell so the
// views consume it via context instead of re-fetching. Value shape:
// { vip: { vip, available, loading }, bonus: { active, campaigns, loading } }.
export const CashierSharedContext = createContext(null);

// Player-currency symbol for a currency code (never a hardcoded euro). Derived
// via Intl; falls back to the code, then the euro sign. Exposed so callers that
// only have a currency code can resolve the symbol without duplicating this.
export function currencySymbolFor(currency) {
  try {
    const parts = new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "EUR" }).formatToParts(0);
    const found = parts.find((p) => p.type === "currency");
    return (found && found.value) || currency || "€";
  } catch (e) {
    return currency || "€";
  }
}

// Global cashier data reused across the redesign: player profile, wallet balance
// + currency (+ FX rates so moneyFormatter works), and CMS cashier_settings.
export function useCashierData() {
  const preview = useContext(CashierPreviewContext);
  const [profile, setProfile] = useState({});
  const [balance, setBalance] = useState(() => (preview ? preview.balance || null : null));
  const [settings, setSettings] = useState(() => {
    const cs = preview && preview.settings;
    if (cs) {
      const parsed = Array.isArray(cs.denominations) ? cs.denominations.filter((v) => Number.isFinite(v) && v > 0) : [];
      const pop = parseInt(cs.popularPreset, 10);
      return {
        denominations: parsed.length ? parsed : DEFAULT_DENOMINATIONS,
        popularPreset: !isNaN(pop) && pop > 0 ? pop : null,
        bannerImage: cs.bannerImage || null,
        bannerLink: cs.bannerLink || null,
      };
    }
    return {
      denominations: DEFAULT_DENOMINATIONS,
      popularPreset: 100,
      bannerImage: null,
      bannerLink: null,
    };
  });

  useEffect(() => {
    // Preview: re-sync balance + settings from the posted config on every change.
    if (preview) {
      setBalance(preview.balance || null);
      const cs = preview.settings;
      if (cs) {
        const parsed = Array.isArray(cs.denominations) ? cs.denominations.filter((v) => Number.isFinite(v) && v > 0) : [];
        const pop = parseInt(cs.popularPreset, 10);
        setSettings({
          denominations: parsed.length ? parsed : DEFAULT_DENOMINATIONS,
          popularPreset: !isNaN(pop) && pop > 0 ? pop : null,
          bannerImage: cs.bannerImage || null,
          bannerLink: cs.bannerLink || null,
        });
      }
      return undefined;
    }
    let alive = true;
    (async () => {
      // /get-balance carries the wallets, currency AND the FX rate table that
      // moneyFormatter.convertCurrencyToFiat reads from local storage.
      try {
        const bal = await api.get("/get-balance");
        if (alive && bal.status === 200 && bal.data) {
          setBalance(bal.data);
          if (bal.data.rates) ls.set("conversionRates", bal.data.rates);
        }
      } catch (e) {
        // balance stays null; the shell shows a neutral placeholder
      }

      try {
        const prof = await api.get("/profile");
        if (alive && prof.status === 200) setProfile(prof.data || {});
      } catch (e) {
        // profile stays empty; bonus-acceptance copy falls back safely
      }

      try {
        const res = await api.get("/initial-settings");
        const cs = res && res.status === 200 ? res.data && res.data.cashier_settings : null;
        if (alive && cs) {
          const parsed = cs.denominations
            ? String(cs.denominations)
                .split(",")
                .map((v) => parseInt(v.trim(), 10))
                .filter((v) => !isNaN(v) && v > 0)
            : [];
          const pop = parseInt(cs.popularPreset, 10);
          setSettings({
            denominations: parsed.length ? parsed : DEFAULT_DENOMINATIONS,
            popularPreset: !isNaN(pop) && pop > 0 ? pop : null,
            bannerImage: cs.bannerImage || null,
            bannerLink: cs.bannerLink || null,
          });
        }
      } catch (e) {
        // keep the default settings on failure; never block the cashier
      }
    })();
    return () => {
      alive = false;
    };
  }, [preview]);

  const wallet = balance && balance.wallets && balance.wallets[0];
  const currency = (wallet && wallet.currency) || (balance && balance.currency_code) || profile.currency || "EUR";
  const balanceFormatted = wallet ? moneyFormatter.balanceFormat(parseFloat(wallet.balance || 0), currency) : null;

  // Player-currency symbol (never a hardcoded euro). Derived from the wallet
  // currency via Intl; falls back to the currency code, then the euro sign.
  const currencySymbol = useMemo(() => currencySymbolFor(currency), [currency]);

  // Reusable player-currency formatter. Every amount shown across the redesign
  // goes through this so the symbol + grouping follow the player's wallet
  // currency (never a hardcoded euro). Falls back to a 0-formatted string.
  const formatMoney = useCallback(
    (amount) => {
      const n = Number(amount);
      return moneyFormatter.balanceFormat(Number.isFinite(n) ? n : 0, currency);
    },
    [currency]
  );

  return { profile, balance, wallet, currency, currencySymbol, balanceFormatted, formatMoney, settings };
}

// Persist the player's next-deposit bonus choice. Shared by the deposit engine
// and the Bonuses view (claim -> save-next-bonus, then switch to Deposit).
export async function saveNextBonus(campaignId) {
  if (previewInert) return false;
  try {
    const res = await api.post("/deposit/save-next-bonus", { campaign_id: campaignId });
    return res.status === 200;
  } catch (e) {
    return false;
  }
}

// Deposit engine: methods + BO sections + eligible campaigns + crypto, and the
// action helpers (persist next bonus, submit deposit, fetch crypto address).
// `amount` drives the campaign eligibility refetch; methods are loaded once with
// a high sentinel so every enabled method is available for the tab layout (the
// engine still applies its country/currency/min filtering server-side).
export function useDepositEngine(amount) {
  const preview = useContext(CashierPreviewContext);
  const [methods, setMethods] = useState(() => (preview ? preview.methods || [] : []));
  const [sections, setSections] = useState(() => (preview ? preview.sections || [] : []));
  const [campaigns, setCampaigns] = useState([]);
  const [cryptoCoins, setCryptoCoins] = useState(() => (preview ? preview.cryptoCoins || [] : []));
  const [cryptoAddresses, setCryptoAddresses] = useState({});
  // Distinct from loaded-empty: true until the first /deposit/methods attempt
  // settles, so the view can show skeleton tiles rather than an empty/fallback
  // bank of methods on the initial paint.
  const [methodsLoading, setMethodsLoading] = useState(() => (preview ? false : true));
  // One idempotency key per deposit attempt, kept stable across retries of the
  // SAME intent (amount+method+campaign) and reset only after a successful
  // submit, so a retry cannot mint a fresh key and double-charge.
  const depositKeyRef = useRef({ sig: null, key: null });

  useEffect(() => {
    // Preview: re-sync from the posted config on every change (the builder posts a
    // fresh config on each edit) — the lazy useState initializer only runs once.
    if (preview) {
      setMethods(preview.methods || []);
      setSections(preview.sections || []);
      setCryptoCoins(preview.cryptoCoins || []);
      setMethodsLoading(false);
      return undefined;
    }
    let alive = true;
    (async () => {
      try {
        const res = await api.post("/deposit/methods", { amount: 1000000 });
        if (alive && res.status === 200) {
          setMethods(res.data.methods || []);
          setSections(Array.isArray(res.data.sections) ? res.data.sections : []);
          // Campaigns come ONLY from the real-amount POST /deposit/campaigns call;
          // the 1M-scoped sentinel here would over-populate and clobber the list.
        }
      } catch (e) {
        // no methods -> the view falls back to the Hub static layout
      }
      try {
        const res = await api.get("/deposit/crypto-coins");
        if (alive && res.status === 200 && res.data && Array.isArray(res.data.coins)) {
          setCryptoCoins(res.data.coins);
        }
      } catch (e) {
        // crypto not configured for this brand/env -> no crypto tab
      }
      if (alive) setMethodsLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [preview]);

  useEffect(() => {
    // Preview: campaigns are computed client-side from the posted bonuses,
    // filtered by the same amount >= minimum_deposit rule the backend applies.
    if (preview) {
      const list = (preview.bonuses || []).filter((c) => (amount || 0) >= (Number(c.minimum_deposit) || 0));
      setCampaigns(list);
      return undefined;
    }
    let alive = true;
    // Debounce so typing an amount fires one campaigns POST, not one per keystroke.
    const timer = setTimeout(async () => {
      try {
        const res = await api.post("/deposit/campaigns", { amount: amount || 0 });
        if (alive && res.status === 200) setCampaigns(res.data.campaigns || []);
      } catch (e) {
        // keep the last known campaigns on a transient failure
      }
    }, 300);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [amount, preview]);

  const fetchCryptoAddress = useCallback(
    async (coin) => {
      // Preview must never mint a live crypto address.
      if (preview) return null;
      if (!coin) return null;
      if (cryptoAddresses[coin]) return cryptoAddresses[coin];
      try {
        const res = await api.post("/deposit/crypto-address", { coin });
        if (res.status === 200 && res.data && res.data.address) {
          setCryptoAddresses((prev) => ({ ...prev, [coin]: res.data }));
          return res.data;
        }
      } catch (e) {
        // address generation failed; caller shows the retry hint
      }
      return null;
    },
    [cryptoAddresses, preview]
  );

  const submitDeposit = useCallback(async ({ amount, method, campaign }) => {
    // Preview must never trigger a live PSP redirect; resolve a neutral non-200.
    if (preview) return { status: 0 };
    // Built EXACTLY like CashierModals' generic redirect deposit, plus a client
    // idempotency key. The key is STABLE per attempt: reused when the intent
    // (amount+method+campaign) is unchanged so a retry cannot double-charge, and
    // regenerated only when the intent changes or after a successful submit.
    const sig = amount + "|" + method + "|" + campaign;
    const store = depositKeyRef.current;
    if (store.sig !== sig || !store.key) {
      depositKeyRef.current = { sig, key: makeIdempotencyKey() };
    }
    const res = await api.post("/deposit", {
      amount,
      method,
      campaign,
      idempotency_key: depositKeyRef.current.key,
    });
    if (res && res.status === 200) depositKeyRef.current = { sig: null, key: null };
    return res;
  }, [preview]);

  return {
    methods,
    sections,
    campaigns,
    cryptoCoins,
    cryptoAddresses,
    methodsLoading,
    fetchCryptoAddress,
    saveNextBonus,
    submitDeposit,
  };
}

// Track the selected bonus by its STABLE id, not a positional index into a list
// that grows/shrinks/reorders as the amount is typed. Resolves the current index
// from the id on every options change and falls back to "no_bonus" (then the
// first option) when the previously-selected campaign is no longer eligible.
export function useBonusSelection(bonusOptions) {
  const options = useMemo(() => bonusOptions || [], [bonusOptions]);
  const [selectedId, setSelectedId] = useState(null);

  const selectedIndex = useMemo(() => {
    if (!options.length) return 0;
    if (selectedId != null) {
      const byId = options.findIndex((o) => String(o.id) === String(selectedId));
      if (byId !== -1) return byId;
    }
    const noBonus = options.findIndex((o) => o.id === "no_bonus");
    return noBonus !== -1 ? noBonus : 0;
  }, [options, selectedId]);

  const selected = options[selectedIndex] || null;
  const selectById = useCallback((id) => setSelectedId(id), []);
  const selectByIndex = useCallback((i) => setSelectedId(options[i] ? options[i].id : null), [options]);

  return { selected, selectedId, selectedIndex, selectById, selectByIndex };
}

// --- Pure engine -> Hub-shape adapters -------------------------------------

// Tile subline: BO promo_text wins (it is the per-method marketing line the
// admin edits), else the min/max in the PLAYER currency (formatted with fmt so
// it carries the wallet symbol, not a bare integer). Badges are NO LONGER folded
// in here: they render as pills on the tile (see MethodTile), so keeping them in
// the subline too would duplicate them. `fmt` is the caller's formatMoney.
function methodSubline(m, fmt) {
  const format = typeof fmt === "function" ? fmt : (v) => String(v);
  if (m.promo_text) return String(m.promo_text);
  // min/max arrive as decimal strings ("0.00"), which are truthy — coerce to a
  // number so a zero/unset minimum reads as "No minimum" instead of "Min $0.00".
  const min = Number(m.min_deposit_in_player_currency) || 0;
  const max = Number(m.max_deposit_in_player_currency) || 0;
  const minPart = min > 0 ? __("Min") + " " + format(min) : __("No minimum");
  const maxPart = max > 0 ? __("Max") + " " + format(max) : "";
  return [minPart, maxPart].filter(Boolean).join(" · ");
}

function mapMethodTile(m, fmt) {
  return {
    name: m.method_id,
    method_id: m.method_id,
    short: m.display_label || m.method || m.method_id,
    label: m.display_label || m.method || m.method_id,
    sub: methodSubline(m, fmt),
    promo: m.promo_text || null,
    logo: m.logo || null,
    min: m.min_deposit,
    max: m.max_deposit == null ? null : m.max_deposit,
    minPlayer: m.min_deposit_in_player_currency,
    maxPlayer: m.max_deposit_in_player_currency,
    section: m.section,
    type: m.type,
    // Canonical-order serving-contract badges, rendered as pills on the tile.
    badges: BADGE_ORDER.filter((b) => (Array.isArray(m.badges) ? m.badges : []).includes(b)),
    isCrypto: false,
  };
}

function cryptoTile(coin) {
  const displayName = moneyFormatter.currencyName(coin) || coin;
  return {
    name: displayName,
    coin,
    short: coin,
    label: displayName,
    sub: coin,
    logo: null,
    isCrypto: true,
  };
}

// Category tabs from BO sections + a separate Crypto tab. Empty categories are
// filtered out (gap #3, FE-side). Crypto is inserted right after the first fiat
// section to mirror the CashierModals order (Bank/Local, Crypto, ...).
export function buildDepositCategories(sections, methods, cryptoCoins) {
  const fiat = (sections || [])
    .map((s) => ({ id: s.slug, label: s.heading || s.slug }))
    .filter((c) => (methods || []).some((m) => m.section === c.id));
  const orphans = (methods || []).filter(
    (m) => m.type !== "Crypto" && !(sections || []).some((s) => s.slug === m.section)
  );
  const cryptoTab = cryptoCoins && cryptoCoins.length ? [{ id: "__crypto", label: __("Crypto") }] : [];
  const otherTab = orphans.length ? [{ id: "__other", label: __("Other") }] : [];
  if (!fiat.length) return [...cryptoTab, ...otherTab];
  return [fiat[0], ...cryptoTab, ...fiat.slice(1), ...otherTab];
}

export function buildDepositMethods(sections, methods, cryptoCoins, fmt) {
  const byCat = {};
  (sections || []).forEach((s) => {
    byCat[s.slug] = (methods || []).filter((m) => m.section === s.slug).map((m) => mapMethodTile(m, fmt));
  });
  const orphans = (methods || []).filter(
    (m) => m.type !== "Crypto" && !(sections || []).some((s) => s.slug === m.section)
  );
  if (orphans.length) byCat.__other = orphans.map((m) => mapMethodTile(m, fmt));
  if (cryptoCoins && cryptoCoins.length) byCat.__crypto = cryptoCoins.map(cryptoTile);
  return byCat;
}

// Map an eligible campaign to the Hub bonus-option shape, keeping the schedule so
// the "you'll play with total" can be computed from the engine rate/cap.
export function buildBonusOptions(campaigns, fmt) {
  const format = typeof fmt === "function" ? fmt : (v) => String(v);
  const opts = (campaigns || []).map((c) => {
    const bc = c.bonus_campaign || {};
    const schedule = c.schedule || null;
    const title = bc.title || __("Bonus");
    const minDeposit = Number(c.minimum_deposit) || 0;
    return {
      id: bc.id,
      title,
      label: title,
      sub: __("Minimum deposit for this bonus") + ": " + format(minDeposit),
      minDeposit,
      schedule,
    };
  });
  opts.push({
    id: "no_bonus",
    title: __("No bonus"),
    label: __("No bonus"),
    sub: __("Deposit without a bonus"),
    minDeposit: 0,
    schedule: null,
  });
  return opts;
}

// Bonus reward (bonus balance) for a given deposit amount, from the engine
// schedule. Percentage bonuses scale with the amount and are capped at
// maximum_bonus; Fixed bonuses are the flat amount. Server is authoritative at
// submit - this is the display-only "you'll play with total" figure (gap #8).
export function bonusReward(option, amount) {
  if (!option) return 0;
  const s = option.schedule;
  if (!s) {
    // Hub static fallback shape (rate/cap) so parity paths still compute.
    if (option.rate) return Math.min(Math.round(amount * option.rate), option.cap || 0);
    return 0;
  }
  let bonus = s.bonus_type === "Percentage" ? (amount * Number(s.bonus || 0)) / 100 : Number(s.bonus || 0);
  const cap = Number(s.maximum_bonus) || 0;
  if (cap > 0) bonus = Math.min(bonus, cap);
  return Math.round(bonus);
}

// Presets from CMS denominations. Per-preset promo flags (More/Superb) are
// DERIVED from the eligible bonus tiers vs the preset amount (gap #7): the two
// highest reward tiers are badged. With denominations but no bonuses, only the
// popular preset is highlighted. Returns null when there is no engine data so
// the caller falls back to the Hub literal PRESETS.
export function derivePresets(denominations, popularPreset, bonusOptions) {
  if (!Array.isArray(denominations) || !denominations.length) return null;
  // Show every configured denomination the admin set in the builder (capped at 8
  // for layout sanity), instead of an arbitrary first-4 — so the player sees what
  // the back-office cashier builder configured.
  const amts = denominations.slice(0, 8);
  const real = (bonusOptions || []).filter((o) => o.id !== "no_bonus" && o.schedule);
  if (real.length) {
    const rewardAt = (amt) =>
      real.reduce((best, o) => (amt < (o.minDeposit || 0) ? best : Math.max(best, bonusReward(o, amt))), 0);
    const positive = amts
      .map((amt) => ({ amt, r: rewardAt(amt) }))
      .filter((x) => x.r > 0)
      .sort((a, b) => a.r - b.r);
    const superbAmt = positive.length ? positive[positive.length - 1].amt : null;
    const moreAmt = positive.length > 1 ? positive[positive.length - 2].amt : null;
    return amts.map((amt) => {
      if (amt === superbAmt) return { amt, flag: { cls: "superb", label: __("Superb Bonus") } };
      if (amt === moreAmt) return { amt, flag: { cls: "more", label: __("More Bonus") } };
      return { amt };
    });
  }
  return amts.map((amt) =>
    // "Most popular amount" (not just "Most Popular") to distinguish this deposit-amount
    // highlight from the per-method "Popular method" badge.
    amt === popularPreset ? { amt, flag: { cls: "more", label: __("Most popular amount") } } : { amt }
  );
}

// --- Withdraw engine -------------------------------------------------------
// Mirrors CashierModals' getWithdrawalMethods + withdraw handling: POST
// /withdraw/methods returns { methods[], details{...} } (details are wallet
// floats in the player's currency); POST /withdraw takes the exact same payload
// (amount, method, extraFields, twofa_code) plus a client idempotency key.
export function useWithdrawEngine() {
  const preview = useContext(CashierPreviewContext);
  const [methods, setMethods] = useState([]);
  const [details, setDetails] = useState({});
  // Distinct from loaded-empty: true until the first /withdraw/methods attempt
  // settles, so the view can show a skeleton rather than the empty state.
  const [withdrawLoading, setWithdrawLoading] = useState(() => (preview ? false : true));
  // Sum of the player's INITIATED (cancellable) withdrawals + its currency, from
  // GET /withdrawals/pending-amount. 0 -> the view omits the pending block.
  const [pending, setPending] = useState({ amount: 0, currency: null });
  // Stable idempotency key per withdraw attempt (see submitDeposit); reset only
  // after a successful submit or when the intent changes.
  const withdrawKeyRef = useRef({ sig: null, key: null });

  const loadMethods = useCallback(async () => {
    try {
      const res = await api.post("/withdraw/methods", { amount: "" });
      if (res.status === 200) {
        setMethods(Array.isArray(res.data.methods) ? res.data.methods : []);
        setDetails(res.data.details && typeof res.data.details === "object" ? res.data.details : {});
      }
    } catch (e) {
      // no methods -> the view shows an honest empty state
    }
  }, []);

  const loadPending = useCallback(async () => {
    try {
      const res = await api.get("/withdrawals/pending-amount");
      if (res.status === 200 && res.data) {
        setPending({ amount: Number(res.data.amount) || 0, currency: res.data.currency || null });
      }
    } catch (e) {
      // no pending endpoint / transient failure -> keep 0 (block stays hidden)
    }
  }, []);

  useEffect(() => {
    if (preview) return undefined;
    let alive = true;
    (async () => {
      await Promise.all([loadMethods(), loadPending()]);
      if (alive) setWithdrawLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [loadMethods, loadPending, preview]);

  // Cancel every pending (INITIATED) withdrawal (POST /withdraw/cancel with no
  // id cancels all and refunds the wallet), then refresh methods/details (new
  // withdrawable) + the pending amount.
  const cancelPending = useCallback(async () => {
    if (preview) return false;
    try {
      const res = await api.post("/withdraw/cancel", {});
      if (res && res.status === 200) {
        await Promise.all([loadMethods(), loadPending()]);
        return true;
      }
    } catch (e) {
      // surfaced to the caller as false
    }
    return false;
  }, [loadMethods, loadPending, preview]);

  // Email a fresh 2FA code to the player (mirrors CashierModals resend2FaCode).
  const resendTwoFa = useCallback(async () => {
    if (preview) return false;
    try {
      const res = await api.post("/resend-twofa");
      return !!(res && res.status === 200);
    } catch (e) {
      return false;
    }
  }, [preview]);

  // Built EXACTLY like CashierModals' withdraw(): same payload keys, plus the
  // client idempotency key (server-side dedupe pending, concern #16 - the
  // in-flight CTA disable is the primary double-submit guard). `accNumber` is
  // the destination wallet ADDRESS (crypto/CRYPTOPAY/WEBPAYMENT/SKRILL:WLT ->
  // backend $request->accNumber) and `tag` is the CoinsPaid tag/memo (backend
  // $request->tag); both are sent at the top level, matching the backend reads.
  const submitWithdraw = useCallback(async ({ amount, method, extraFields, twoFaCode, accNumber, tag }) => {
    // Preview must never submit a live withdrawal; resolve a neutral non-200.
    if (preview) return { status: 0 };
    const fields = extraFields || {};
    const acc = accNumber != null ? String(accNumber) : "";
    const memo = tag != null ? String(tag) : "";
    const sig = amount + "|" + method + "|" + JSON.stringify(fields) + "|" + acc + "|" + memo;
    const store = withdrawKeyRef.current;
    if (store.sig !== sig || !store.key) {
      withdrawKeyRef.current = { sig, key: makeIdempotencyKey() };
    }
    const payload = {
      amount,
      method,
      extraFields: fields,
      twofa_code: twoFaCode || "",
      idempotency_key: withdrawKeyRef.current.key,
    };
    if (acc) payload.accNumber = acc;
    if (memo) payload.tag = memo;
    const res = await api.post("/withdraw", payload);
    if (res && res.status === 200) withdrawKeyRef.current = { sig: null, key: null };
    return res;
  }, [preview]);

  return { methods, details, withdrawLoading, pending, submitWithdraw, cancelPending, resendTwoFa };
}

// Map a withdraw method to the MethodTile shape. min_withdrawal is in base
// currency (USD per CashierModals); converted ONCE to the player's currency so
// the tile subline, the field "Min" line, and the client validation all agree
// for non-USD players. minWithdrawalPlayer is the numeric player-currency value
// callers must use for the field + the `< min` check (minWithdrawalBase is the
// raw base number, kept only for reference).
export function buildWithdrawMethods(methods, currency) {
  return (methods || []).map((m) => {
    const logo = typeof m.logo === "string" && /^https?:/i.test(m.logo) ? m.logo : null;
    const baseMin = Number(m.min_withdrawal) || 0;
    const hasMin = baseMin > 0;
    const converted = moneyFormatter.getFiatValue(baseMin, "USD", currency);
    const minPlayer = typeof converted === "number" && Number.isFinite(converted) ? converted : baseMin;
    return {
      name: m.method_id,
      method_id: m.method_id,
      label: m.method || m.method_id,
      short: m.method || m.method_id,
      sub: hasMin ? __("Min") + " " + moneyFormatter.balanceFormat(minPlayer, currency) : __("No minimum"),
      logo,
      minWithdrawal: minPlayer,
      minWithdrawalPlayer: minPlayer,
      minWithdrawalBase: baseMin,
    };
  });
}

// Sidebar/withdraw-card alignment: the shell Sidebar must show the SAME
// "Withdrawable" figure as the Withdraw card. The card reads the engine's
// details.total_withdrawal_amount (POST /withdraw/methods) - the true cash a
// player can withdraw right now (net of pending payouts / locked bonus), which
// can differ from redux cash_balance under sticky-bonus modes. This lightweight
// hook fetches ONLY that figure (in player currency) for the shell. It is a
// separate read from WithdrawView's own engine (that view is out of this pass'
// scope and needs the full engine for its mutations); both read the same
// endpoint+field, so the two figures agree. The shell falls back to redux
// cash_balance until this resolves.
export function useWithdrawableAmount() {
  const preview = useContext(CashierPreviewContext);
  const [withdrawable, setWithdrawable] = useState(() =>
    preview ? (preview.balance && preview.balance.cash_balance != null ? preview.balance.cash_balance : 0) : null
  );

  useEffect(() => {
    if (preview) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await api.post("/withdraw/methods", { amount: "" });
        if (alive && res.status === 200 && res.data && res.data.details) {
          const n = Number(res.data.details.total_withdrawal_amount);
          if (Number.isFinite(n)) setWithdrawable(n);
        }
      } catch (e) {
        // fall back to redux cash_balance in the shell
      }
    })();
    return () => {
      alive = false;
    };
  }, [preview]);

  return { withdrawable };
}

// --- Active bonus + available bonuses --------------------------------------
// GET /bonus/active -> { has_active_bonus, can_cancel, wagering_requirement,
// wagering_achieved, bonus_balance }. Available bonuses come from the eligible-
// campaigns endpoint via its disregard-amount variant (POST /deposit/campaigns/1):
// the plain amount:0 call is guarded server-side and returns [], so the list and
// the sidebar badge would always be empty. The disregard route returns every
// eligible campaign regardless of amount, with the SAME { campaigns } shape.
// SOURCE hook: fetched ONCE in the shell; views read the result from context via
// useBonusData() so there is no duplicate /bonus/active or /deposit/campaigns.
export function useBonusDataSource() {
  const preview = useContext(CashierPreviewContext);
  const [active, setActive] = useState(() => (preview ? (preview.bonus && preview.bonus.active) ?? null : null));
  const [campaigns, setCampaigns] = useState(() => (preview ? preview.bonuses ?? DEFAULT_PREVIEW_BONUSES : []));
  const [loading, setLoading] = useState(() => (preview ? false : true));

  useEffect(() => {
    if (preview) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/bonus/active");
        if (alive && res.status === 200 && res.data && typeof res.data === "object") {
          setActive(res.data);
        }
      } catch (e) {
        // no active-bonus data -> top card falls back to an empty state
      }
      try {
        // "/1" => disregard_amount truthy => all eligible campaigns.
        const res = await api.post("/deposit/campaigns/1", {});
        if (alive && res.status === 200 && Array.isArray(res.data.campaigns)) {
          setCampaigns(res.data.campaigns);
        }
      } catch (e) {
        // no eligible campaigns -> the available list renders empty
      }
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [preview]);

  return { active, campaigns, loading };
}

// Consumer: read the shell's single fetch from context. Safe defaults when
// rendered outside the provider (not expected in the redesign).
export function useBonusData() {
  const ctx = useContext(CashierSharedContext);
  return (ctx && ctx.bonus) || { active: null, campaigns: [], loading: false };
}

// --- VIP (gamification) ----------------------------------------------------
// GET /rewards/dashboard is the FLAT-payload EXCEPTION: read body.data.vip.
// 404 = gamification disabled -> return null so the view shows an empty state.
// SOURCE hook: fetched ONCE in the shell; VipView reads it from context via
// useVipData() so /rewards/dashboard is not requested twice.
export function useVipDataSource() {
  const preview = useContext(CashierPreviewContext);
  const [vip, setVip] = useState(() => (preview ? (preview.vip !== undefined ? preview.vip : DEFAULT_PREVIEW_VIP) : null));
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(() => (preview ? false : true));

  useEffect(() => {
    if (preview) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/rewards/dashboard");
        if (!alive) return;
        if (res.status === 200 && res.data && res.data.data && res.data.data.vip) {
          setVip(res.data.data.vip);
        } else if (res.status === 404) {
          setAvailable(false);
        }
      } catch (e) {
        if (alive) setAvailable(false);
      }
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [preview]);

  return { vip, available, loading };
}

// Consumer: read the shell's single fetch from context.
export function useVipData() {
  const ctx = useContext(CashierSharedContext);
  return (ctx && ctx.vip) || { vip: null, available: true, loading: false };
}

// --- History ---------------------------------------------------------------
// POST /history/transactions (reuses TransactionHistory.js's form + response
// handling): { data[], paginationData }. Type (historyFor: All/Deposits/
// Withdrawals) + a date range (startDate/endDate, "YYYY-MM-DD") are the only
// supported filters; this endpoint reads DepositWithdrawal only, so bonus/
// free-spin rows are never returned (honest gap, not faked). Rows are pre-
// formatted (amount already carries the currency symbol). `error` is distinct
// from an empty result so the view can tell "load failed" from "no rows".
const HISTORY_DEFAULT_FILTERS = { historyFor: "All", startDate: null, endDate: null };
export function useHistory() {
  const preview = useContext(CashierPreviewContext);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState(HISTORY_DEFAULT_FILTERS);

  useEffect(() => {
    if (preview) return undefined;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.post("/history/transactions", {
          historyFor: filters.historyFor || "All",
          startDate: filters.startDate || null,
          endDate: filters.endDate || null,
          page,
        });
        if (alive && res.status === 200) {
          const fresh = Array.isArray(res.data.data) ? res.data.data : [];
          // page 1 replaces; later pages append (load-more).
          setRows((prev) => (page === 1 ? fresh : prev.concat(fresh)));
          const pg = Array.isArray(res.data.paginationData) ? res.data.paginationData : [];
          setHasMore(pg.some((p) => p && p.type === "next_page"));
          setError(false);
        } else if (alive) {
          if (page === 1) setRows([]);
          setHasMore(false);
          setError(true);
        }
      } catch (e) {
        if (alive) {
          if (page === 1) setRows([]);
          setHasMore(false);
          setError(true);
        }
      }
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [page, filters, preview]);

  const loadMore = useCallback(() => setPage((p) => p + 1), []);

  // Applying a filter resets to page 1 and replaces the result set. Both state
  // updates land in one render, so the effect fires once with the new filters.
  const applyFilters = useCallback((next) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, ...next }));
  }, []);

  return { rows, hasMore, loading, error, filters, applyFilters, loadMore };
}

// Map a /history/transactions row to the Hub TxRow shape. Sign is derived from
// the transaction type (deposit/credit = +, withdrawal = -); the status chip
// uses the backend's status string with a class from the status_code vocabulary
// (only st-done / st-pend exist in the parity SCSS).
const HISTORY_DONE_CODES = { deposit: [1], withdrawal: [1, 3, 7] };
export function mapHistoryRow(row) {
  const type = String(row.type || "").toLowerCase();
  const isOut = type.indexOf("withdraw") !== -1;
  const isBonus =
    !isOut &&
    (type.indexOf("bonus") !== -1 ||
      type.indexOf("free spin") !== -1 ||
      type.indexOf("freespin") !== -1 ||
      type.indexOf("cashback") !== -1);
  const doneCodes = isOut ? HISTORY_DONE_CODES.withdrawal : HISTORY_DONE_CODES.deposit;
  const done = doneCodes.indexOf(Number(row.status_code)) !== -1;
  const statusText = String(row.status || "").toLowerCase();
  const failed = /fail|declin|cancel|reject|void/.test(statusText);
  const sign = isOut ? "−" : "+";
  return {
    type: isOut ? "out" : isBonus ? "bon" : "in",
    title: row.type + (row.method ? " · " + row.method : ""),
    when: row.action_date,
    value: sign + (row.amount || ""),
    vClass: isOut ? "neg" : "pos",
    status: row.status,
    sClass: done ? "st-done" : failed ? "st-fail" : "st-pend",
  };
}

// --- Limits (Responsible Gaming) -------------------------------------------
// Product-owner decision: the cashier surfaces ONLY the DEPOSIT limit. Loss,
// session and self-exclusion limits belong in the player account (the RG
// settings page), so this hook no longer fetches them. Reads GET /deposit-limit
// (AccessControlController@getDepositLimit -> active CashLimit row or null, in
// player currency) and wires the SET endpoint (POST /deposit-limit).
//
// Change rule (AccessControlController@setDepositLimit): the set is REJECTED
// with "You are not allowed to change the limit at this moment." while any
// non-expired deposit limit exists. So a new limit can only be set when none is
// active; once active it is locked until it expires. The view reflects this
// honestly (form shown only when no active limit; the engine message is surfaced
// verbatim if a blocked set slips through). Enforcement: the limit caps the
// total deposits within the [start_date, end_date] window (DepositHelpers).
export function useLimits() {
  const preview = useContext(CashierPreviewContext);
  const [depositLimit, setDepositLimit] = useState(null);
  // Distinct from loaded-empty: true until the read settles, so the view can
  // show a skeleton rather than flashing "no limit set".
  const [loading, setLoading] = useState(() => (preview ? false : true));
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await api.get("/deposit-limit");
      if (res.status === 200) setDepositLimit(res.data && res.data.id ? res.data : null);
    } catch (e) {
      // keep the last known value on a transient failure
    }
  }, []);

  useEffect(() => {
    if (preview) return undefined;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/deposit-limit");
        if (alive && res.status === 200) setDepositLimit(res.data && res.data.id ? res.data : null);
      } catch (e) {
        // leave null -> the view shows the "not set" state
      }
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [preview]);

  // Set a new deposit limit. Payload mirrors AccessControl.js (limit_amount +
  // start_date/end_date). Returns { ok, message } so the view can surface the
  // engine's own success/blocked message. On success the active limit reloads.
  const submitDepositLimit = useCallback(
    async ({ amount, startDate, endDate }) => {
      if (preview) return { ok: false, message: "" };
      setSaving(true);
      let result = { ok: false, message: "" };
      try {
        const res = await api.post("/deposit-limit", {
          limit_amount: amount,
          start_date: startDate,
          end_date: endDate,
        });
        const message = (res && res.data && res.data.message) || "";
        if (res && res.status === 200) {
          result = { ok: true, message };
          await reload();
        } else {
          result = { ok: false, message };
        }
      } catch (e) {
        result = { ok: false, message: "" };
      }
      setSaving(false);
      return result;
    },
    [reload, preview]
  );

  return { depositLimit, loading, saving, submitDepositLimit };
}
